import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, X, Volume2, VolumeX } from "lucide-react";
import SoulOrb from "@/components/SoulOrb";
import {
  getPinnedAffirmation, clearPinnedAffirmation, daysRemaining,
} from "@/lib/pinnedAffirmation";
import { canSpeak, speak, stopSpeaking, isSpeaking } from "@/lib/readAloud";

/**
 * PinnedAffirmationCard — surfaces the member's currently-pinned
 * affirmation on the Home page. Renders nothing when no pin exists,
 * or when the 7-day window has quietly expired. Includes a small orb
 * that pulses softly on first paint (a warm hello) and can be tapped
 * to release the pin.
 */
export default function PinnedAffirmationCard() {
  const [pin, setPin] = useState(() => getPinnedAffirmation());
  const [pulseSeed, setPulseSeed] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  // Stop any playback if the component unmounts or the pinned line changes.
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => { if (speaking) { stopSpeaking(); setSpeaking(false); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin?.text]);

  useEffect(() => {
    const refresh = () => setPin(getPinnedAffirmation());
    window.addEventListener("pinned-affirmation-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("pinned-affirmation-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // One soft pulse when the pinned card first renders.
  useEffect(() => {
    if (pin) setPulseSeed((p) => p + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin?.text]);

  if (!pin) return null;

  const days = daysRemaining(pin.expires_at);

  const unpin = () => {
    stopSpeaking();
    setSpeaking(false);
    clearPinnedAffirmation();
    setPin(null);
    toast.success("Released.");
  };

  const toggleRead = () => {
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    if (!canSpeak()) {
      toast.error("Read aloud isn't available on this browser.");
      return;
    }
    const ok = speak(pin.text, {
      rate: 0.85,
      pitch: 1.0,
      onEnd: () => setSpeaking(false),
    });
    if (ok) setSpeaking(true);
  };

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-6 sm:p-7 reveal"
      data-testid="home-pinned-affirmation"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -left-16 w-64 h-64 rounded-full opacity-[0.08]"
        style={{ background: "radial-gradient(circle, hsl(var(--terracotta)) 0, transparent 65%)" }}
      />

      <div className="relative flex items-start gap-4 sm:gap-6">
        <SoulOrb
          size="md"
          pulseSeed={pulseSeed}
          ariaLabel="Soul orb — carrying your pinned affirmation"
          testid="home-pinned-affirmation-orb"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))]">
            <Pin size={11} />
            <span data-testid="home-pinned-affirmation-eyebrow">Pinned this week</span>
            {pin.category && (
              <span className="text-[hsl(var(--muted-foreground))] normal-case tracking-normal">· {pin.category}</span>
            )}
          </div>
          <p
            className="mt-2 font-display italic text-xl sm:text-2xl leading-snug text-[hsl(var(--fg))]"
            data-testid="home-pinned-affirmation-text"
          >
            {pin.text}
          </p>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
              data-testid="home-pinned-affirmation-days"
            >
              {days === 0
                ? "Releases today"
                : days === 1
                  ? "One day remaining"
                  : `${days} days remaining`}
            </span>
            <div className="flex items-center gap-2">
              {canSpeak() && (
                <button
                  type="button"
                  onClick={toggleRead}
                  aria-label={speaking ? "Stop reading" : "Read aloud"}
                  aria-pressed={speaking}
                  data-testid="home-pinned-affirmation-speaker"
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] tab-link ${
                    speaking
                      ? "text-[hsl(var(--terracotta))]"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                  }`}
                >
                  {speaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
                  {speaking ? "Stop" : "Read aloud"}
                </button>
              )}
              <button
                type="button"
                onClick={unpin}
                aria-label="Release this pin"
                data-testid="home-pinned-affirmation-unpin"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] tab-link"
              >
                <X size={11} />
                Release
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
